import type { CSSProperties } from 'react';
import React from 'react';

import { Typography } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

const useButtonStyles = makeStyles<{ position: 'left' | 'right'; selected: boolean }>()(
  (theme, { position, selected }) => ({
    button: (() => {
      const borderColor = `1px solid ${theme.palette.primary.main}`;
      const borderRadiusString = position === 'left' ? '3px 0px 0px 3px' : '0px 3px 3px 0px';
      const borderKey: keyof CSSProperties = position === 'left' ? 'borderLeft' : 'borderRight';

      return {
        alignItems: 'center',
        backgroundColor: selected ? theme.palette.primary.main : undefined,
        borderBottom: borderColor,
        [borderKey]: borderColor,
        borderRadius: borderRadiusString,
        borderTop: borderColor,
        color: selected ? theme.palette.common.white : theme.palette.primary.main,
        cursor: 'pointer',
        display: 'flex',
        padding: theme.spacing(1),
      };
    })(),
    text: {
      fontWeight: 600,
    },
  })
);

function TextTabButton({
  className,
  onClick,
  position,
  selected,
  text,
}: {
  onClick: () => void;
  position: 'left' | 'right';
  selected: boolean;
  text: string;
  className?: string;
}): JSX.Element {
  const { classes } = useButtonStyles({ position, selected });

  return (
    <div className={clsx(classes.button, className)} onClick={onClick}>
      <Typography className={classes.text} color="inherit">
        {text}
      </Typography>
    </div>
  );
}

const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
}));

interface TextTabSwitchProps {
  leftText: string;
  onLeftClick: () => void;
  onRightClick: () => void;
  rightText: string;
  selectedSide: 'left' | 'right';
  className?: string;
  leftButtonClassName?: string;
  rightButtonClassName?: string;
}

export function TextTabSwitch({
  className,
  leftButtonClassName,
  leftText,
  onLeftClick,
  onRightClick,
  rightButtonClassName,
  rightText,
  selectedSide,
}: TextTabSwitchProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={clsx(classes.container, className)}>
      <TextTabButton
        className={leftButtonClassName}
        position="left"
        selected={selectedSide === 'left'}
        text={leftText}
        onClick={onLeftClick}
      />
      <TextTabButton
        className={rightButtonClassName}
        position="right"
        selected={selectedSide === 'right'}
        text={rightText}
        onClick={onRightClick}
      />
    </div>
  );
}
